import { useSelector } from 'react-redux'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { ff, flags } from 'functions/roleBasedAccessUtils'
import { AppIsInTestEnvironment } from 'functions/environmentUtils'
import { ModalProvider } from 'components/Modals/ModalContext'

import ModalOverlay from 'components/Modals/ModalOverlay'
import MasterContextProvider from 'context/MasterContextProvider'

import Login from 'views/Login'
import FullscreenLoading from 'components/Loading/FullscreenLoading'
import InvalidAuth0ConnectionView from 'views/InvalidAuth0ConnectionView'
import IncorrectAccountTypeView from "views/IncorrectAccountTypeView";

import CoachAccountSetupOptionsView from 'views/Onboarding/CoachAccountSetupOptionsView'
import CreateOrganizationView from 'views/Onboarding/CreateOrganizationView'
import JoinOrganizationView from 'views/Onboarding/JoinOrganizationView'

import Home from 'views/Home'

import CoachList from 'views/Coach/CoachList'
import CoachOnboardingOptionsView from 'views/Onboarding/CoachOnboardingOptionsView'
import CoachInviteView from 'views/Onboarding/CoachInviteView'

import AthleteInviteView from 'views/Onboarding/AthleteInviteView'
import AthleteOnboardingOptionsView from 'views/Onboarding/AthleteOnboardingOptionsView'
import AthleteCreateView from 'views/Onboarding/AthleteCreateView'
import SyncTrainingPeaksAthletes from 'views/Athlete/SyncTrainingPeaksAthletes'
import AthleteList from 'views/Athlete/AthleteList'
import KinesisProfileRouter from 'views/Profile/KinesisProfileRouter'
import KinesisProfileSettings from 'views/Profile/KinesisProfileSettings'
import GroupAthletesView from 'views/Profile/GroupAthletesView'
import ScheduleView from 'views/Profile/ScheduleView'
import FitnessProfileView from 'views/Profile/FitnessProfileView'
import EquipmentView from 'views/Profile/EquipmentView'
import InjuriesView from 'views/Profile/InjuriesView'
import BulkEditView from 'views/Athlete/BulkEditView'
import ActiveWorkoutPreview from 'views/Athlete/ActiveWorkoutPreview'
import ActiveWorkout from 'views/Athlete/ActiveWorkout'
import WorkoutV5PreviewView from 'views/Workout/WorkoutV5PreviewView'
import ActiveWorkoutV5 from 'views/Workout/ActiveWorkoutV5'
import GroupList from 'views/Group/GroupList'
import CreateGroup from 'views/Group/CreateGroup'
import EditGroup from 'views/Group/EditGroup'
import Assessments from 'views/Asssessment/Assessments'
import CompletedAssessmentSummary from 'views/CompletedWorkouts/CompletedAssessmentSummary'
import WorkoutList from 'views/CompletedWorkouts/WorkoutList'
import CompletedWorkoutSummary from 'views/CompletedWorkouts/CompletedWorkoutSummary'
import WorkoutV5SummaryView from 'views/CompletedWorkouts/WorkoutV5SummaryView'
import ManageIntegrationsView from 'views/Settings/ManageIntegrationsView'
import Resources from 'views/Resources/Resources'
import PDFView from 'views/Content/PDFView'
import OrganizationSettings from 'views/Settings/OrganizationSettings'
import EditOrganizationDetailsView from 'views/Settings/EditOrganizationDetailsView'
import BillingOverview from 'views/Settings/BillingOverview'
import UserProfile from 'views/User/UserProfile'
import ReduxSliceResetPage from 'views/ReduxSliceResetPage'

import OrganizationDirectory from 'views/Sales/OrganizationDirectory'
import UserDirectory from 'views/Sales/UserDirectory'
import ProgramDirectoryView from 'views/Sales/ProgramDirectoryView'
import ProgramPlaygroundView from 'views/Program/ProgramPlaygroundView'
import ProgramCanvasView from 'views/Program/ProgramCanvasView'
import ProgramSettingsView from 'views/Program/ProgramSettingsView'
import ExerciseDirectory from 'views/Exercises/ExerciseDirectory'
import ExpandedExerciseData from 'views/Exercises/ExpandedExerciseData'
import UserFeedbackView from 'views/Feedback/UserFeedbackView'
import AppStoreSubscriptionDirectory from 'views/Sales/AppStoreSubscriptionDirectory'
import EmailInviteList from 'views/EmailInvites/EmailInviteList'
import BranchClicks from 'views/Sales/BranchClicks'

import BoilerplateProgramDirectoryView from 'views/Program/BoilerplateProgramDirectoryView'
import MesoVolumeView from 'views/Program/MesoVolumeView'
import ExerciseCollectionsView from 'views/ExerciseCollection/ExerciseCollectionsView'
import ExerciseCollectionView from 'views/ExerciseCollection/ExerciseCollectionView'
import ProgramSnapshotGenerator from 'views/Generation/ProgramSnapshotGenerator'
import ProgramPeriodizationGenerator from 'views/Generation/ProgramPeriodizationGenerator'
import CreateCustomExercise from 'views/Exercises/CreateCustomExercise'
import CreateWorkoutTemplate from 'views/WorkoutTemplates/CreateWorkoutTemplate'
import ReportingOverview from 'views/InternalReports/ReportingOverview'
import ReportingUserActivity from 'views/InternalReports/ReportingUserActivity'

import APITestingView from 'views/Testing/APITestingView'
import UIDesignSpecView from 'views/Testing/UIDesignSpecView'

import RouteNotFoundView from 'views/RouteNotFoundView'

import AuthenticatedUserErrorView from 'views/AuthenticatedUserErrorView'

export default function CoachDashboardRouter({ viewGroup = 'loading-user' }) {

    const HasOrganizationPaymentMethodBeenAdded = () => {
        if (requirePaymentMethodToInviteAthletes === 0) { 
            return true
        }

        if (requirePaymentMethodToInviteAthletes === 1) {
            if (organizationAdminStripeCustomerId === '') {
                return false
            }

            return true
        }
    }

    const userType = useSelector((state) => state.user.type)
    const adminToolsVisible = useSelector((state) => state.user.adminToolsVisible)
    const requirePaymentMethodToInviteAthletes = useSelector((state) => state.user.requirePaymentMethodToInviteAthletes)
    const organizationAdminStripeCustomerId = useSelector((state) => state.user.organizationAdminStripeCustomerId)
    const enableCoachRunningWorkouts = useSelector((state) => state.user.enableCoachRunningWorkouts)
    const enableKinesisUserGroups = useSelector((state) => state.user.enableKinesisUserGroups)

    const canAccessBillingSettings = ff(flags.canAccessBillingSettings, { userType: userType })
    const canAccessKinesisUserGroups = ff(flags.canAccessKinesisUserGroups, { userType: userType, enableKinesisUserGroups: enableKinesisUserGroups })
    const canAccessOrganzationInfoSettings = ff(flags.canAccessOrganzationInfoSettings, { userType: userType })
    const canAccessSidebarAdminTools = ff(flags.canAccessSidebarAdminTools, { userType: userType, adminToolsVisible: adminToolsVisible })    
    const canAccessSidebarSalesTools = ff(flags.canAccessSidebarSalesTools, { userType: userType, adminToolsVisible: adminToolsVisible })
    const canRunWorkouts = ff(flags.canRunWorkouts, { userType: userType, enableCoachRunningWorkouts: enableCoachRunningWorkouts })
    const hasOrganizationPaymentMethodBeenAdded = HasOrganizationPaymentMethodBeenAdded()
    const appIsInTestEnvironment = AppIsInTestEnvironment()

    return (
        <BrowserRouter>
            <ModalProvider>
                <MasterContextProvider>
                    <Routes>
                        {viewGroup === 'unauthenticated-user' && <Route path="*" element={<Login />} />}

                        {viewGroup === 'loading-user' && <Route path="*" element={<FullscreenLoading />} />}
                        
                        {viewGroup === 'incorrect-auth0-connection' && <Route path="*" element={<InvalidAuth0ConnectionView />} />}
                        
                        {viewGroup === 'incorrect-account-type' && <Route path="*" element={<IncorrectAccountTypeView />} />}

                        {viewGroup === 'authenticated-user-onboarding' && <Route path='*' element={<CoachAccountSetupOptionsView />} />}

                        {viewGroup === 'authenticated-user-onboarding' && <Route path='/organization/create' element={<CreateOrganizationView />} />}

                        {viewGroup === 'authenticated-user-onboarding' && <Route path='/organization/join' element={<JoinOrganizationView />} />}

                        {viewGroup === 'authenticated-user' && (
                            <>
                                <Route path="/" element={<Home />} />

                                <Route path="/home" element={<Home />} />

                            {/* Coach routes */}

                                <Route path="/coaches" element={<CoachList />} />
                                
                                {hasOrganizationPaymentMethodBeenAdded && <Route path="/onboarding/coaches/options" element={<CoachOnboardingOptionsView />} />}
                                
                                {hasOrganizationPaymentMethodBeenAdded && <Route path="/onboarding/coaches/invite" element={<CoachInviteView />} />}

                            {/* Group routes */}

                                {canAccessKinesisUserGroups && <Route path="/groups" element={<GroupList />} />}

                                {canAccessKinesisUserGroups && <Route path="/groups/create" element={<CreateGroup />} />}

                                {canAccessKinesisUserGroups && <Route path="/groups/edit" element={<EditGroup />} />}

                                {canAccessKinesisUserGroups && <Route path="/group/:urlGroupId" element={<KinesisProfileRouter propProfileType="group" />} />}
                                
                                {canAccessKinesisUserGroups && <Route path="/group/:urlGroupId/bulkEdit" element={<BulkEditView propProfileType="group" />} />}

                                {canAccessKinesisUserGroups && <Route path="/group/:urlGroupId/settings" element={<KinesisProfileSettings propProfileType="group" />} />}

                                {canAccessKinesisUserGroups && <Route path="/group/:urlGroupId/athletes" element={<GroupAthletesView />} />}

                                {canAccessKinesisUserGroups && <Route path="/group/:urlGroupId/schedule" element={<ScheduleView propProfileType="group" />} />}

                                {canAccessKinesisUserGroups && <Route path="/group/:urlGroupId/fitnessProfile" element={<FitnessProfileView propProfileType="group" />} />}

                                {canAccessKinesisUserGroups && <Route path="/group/:urlGroupId/equipment" element={<EquipmentView propProfileType="group" />} />}

                            {/* Athlete routes */}

                                <Route path="/athletes" element={<AthleteList />} />

                                {hasOrganizationPaymentMethodBeenAdded && <Route path="/onboarding/athletes/options" element={<AthleteOnboardingOptionsView />} />}

                                {hasOrganizationPaymentMethodBeenAdded && <Route path="/onboarding/athletes/invite" element={<AthleteInviteView />} />}

                                {hasOrganizationPaymentMethodBeenAdded && <Route path="/onboarding/athletes/create" element={<AthleteCreateView />} />}

                                {hasOrganizationPaymentMethodBeenAdded && <Route path="/onboarding/athletes/trainingPeaks" element={<SyncTrainingPeaksAthletes />} />}

                                <Route path="/athlete/:urlAthleteId" element={<KinesisProfileRouter propProfileType="individual" />} />

                                <Route path="/athlete/:urlAthleteId/bulkEdit" element={<BulkEditView propProfileType="individual" />} />

                                <Route path="/athlete/:urlAthleteId/settings" element={<KinesisProfileSettings propProfileType="individual" />} />

                                <Route path="/athlete/:urlAthleteId/schedule" element={<ScheduleView propProfileType="individual" />} />

                                <Route path="/athlete/:urlAthleteId/fitnessProfile" element={<FitnessProfileView propProfileType="individual" />} />

                                <Route path="/athlete/:urlAthleteId/equipment" element={<EquipmentView propProfileType="individual" />} />

                                <Route path="/athlete/:urlAthleteId/injuries" element={<InjuriesView propProfileType="individual" />} />

                            {/* Active workout v4 routes */}

                                {canRunWorkouts && <Route path="/active/workout/preview" element={<ActiveWorkoutPreview />} />}
                                
                                {canRunWorkouts && <Route path="/active/workout/group/:groupId" element={<ActiveWorkout profileType="group" />} />}
                                
                                {canRunWorkouts && <Route path="/active/workout/individual/:athleteId" element={<ActiveWorkout profileType="individual" />} />}

                            {/* Active workout v5 routes */}

                                {canRunWorkouts && <Route path="/workout/:urlWorkoutId/preview" element={<WorkoutV5PreviewView />} />}

                                {canRunWorkouts && <Route path="/workout/:urlWorkoutId/active" element={<ActiveWorkoutV5 />} />}

                            {/* Assessment routes */}
                                
                                <Route path="/assessments" element={<Assessments />} />

                                <Route path="/assessment/summary" element={<CompletedAssessmentSummary />} />

                            {/* Workout routes */}

                                <Route path="/workouts" element={<WorkoutList />} />

                                <Route path="/workout/summary" element={<CompletedWorkoutSummary />} />

                                <Route path="/workout/:urlWorkoutId/summary" element={<WorkoutV5SummaryView />} />

                            {/* Resource routes */}

                                <Route path="/resources" element={<Resources />} />

                                <Route path="/resources/pdf" element={<PDFView />} />

                            {/* Settings routes */}

                                <Route path="/settings" element={<OrganizationSettings />} />

                                {canAccessOrganzationInfoSettings && <Route path="/settings/details" element={<EditOrganizationDetailsView />} />}

                                {canAccessBillingSettings && <Route path="/settings/billing" element={<BillingOverview />} />}
                                
                                <Route path="/settings/integrations" element={<ManageIntegrationsView />} />

                                <Route path="/user/profile" element={<UserProfile />} />
                                
                                <Route path="/success" element={<ReduxSliceResetPage />} />

                            {/* Sales routes */}

                                {canAccessSidebarSalesTools && <Route path="/feedback" element={<UserFeedbackView />} />}

                                {canAccessSidebarSalesTools && <Route path="/appStoreSubscriptionDirectory" element={<AppStoreSubscriptionDirectory />} />}

                                {canAccessSidebarSalesTools && <Route path="/branchClicks" element={<BranchClicks />} />}

                                {canAccessSidebarSalesTools && <Route path="/email/invites" element={<EmailInviteList />} />}

                                {canAccessSidebarSalesTools && <Route path="/reporting/overview" element={<ReportingOverview />} />}

                                {canAccessSidebarSalesTools && <Route path="/reporting/user/activity" element={<ReportingUserActivity />} />}

                            {/* Data routes */}

                                {canAccessSidebarAdminTools && <Route path="/organizations" element={<OrganizationDirectory embeddedInModal={false} />} />}
                                
                                {canAccessSidebarAdminTools && <Route path="/users" element={<UserDirectory />} />}
                                
                                {canAccessSidebarAdminTools && <Route path="/programs" element={<ProgramDirectoryView />} />}
                                
                            {/* Program Generation routes */}

                                {canAccessSidebarAdminTools && <Route path="/boilerplates" element={<BoilerplateProgramDirectoryView />} />}

                                {canAccessSidebarAdminTools && <Route path="/program/:urlProgramId/playground" element={<ProgramPlaygroundView />} />}
                                
                                {canAccessSidebarAdminTools && <Route path="/program/:urlProgramId/canvas" element={<ProgramCanvasView />} />}
                                
                                {canAccessSidebarAdminTools && <Route path="/program/:urlProgramId/settings" element={<ProgramSettingsView />} />}

                                {canAccessSidebarAdminTools && <Route path="/mesoVolume/:urlMesoVolumeId" element={<MesoVolumeView />} />}

                                {canAccessSidebarAdminTools && <Route path="/exerciseCollections" element={<ExerciseCollectionsView />} />}
                                
                                {canAccessSidebarAdminTools && <Route path="/exerciseCollection/:urlExerciseCollectionId" element={<ExerciseCollectionView />} />}

                                {canAccessSidebarAdminTools && <Route path="/exercises" element={<ExerciseDirectory />} />}

                                {canAccessSidebarAdminTools && <Route path="/exerciseData" element={<ExpandedExerciseData />} />}
                                
                                {canAccessSidebarAdminTools && <Route path="/exercise/create" element={<CreateCustomExercise />} />}
                                
                                {canAccessSidebarAdminTools && <Route path="/program/snapshot/generator" element={<ProgramSnapshotGenerator />} />}
                                
                                {canAccessSidebarAdminTools && <Route path="/program/periodization/generator" element={<ProgramPeriodizationGenerator />} />}
                                
                                {canAccessSidebarAdminTools && <Route path="/workout/template/create" element={<CreateWorkoutTemplate />} />}
                                
                            {/* Test routes */}

                                {appIsInTestEnvironment && <Route path="/testing/api" element={<APITestingView />} />}

                                {appIsInTestEnvironment && <Route path="/testing/uiDesignSpec" element={<UIDesignSpecView />} />}

                            {/* Invalid routes */}

                                <Route path="*" element={<RouteNotFoundView />} />
                            </>
                        )}

                        {viewGroup === 'authenticated-user-error' && <Route path="*" element={<AuthenticatedUserErrorView />} />}
                    </Routes>
                    <ModalOverlay />
                </MasterContextProvider>
            </ModalProvider>
        </BrowserRouter>
    )
}